import React, { useEffect, useState, useMemo } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import WebService from "../../../Services/WebService";
import "./LessonResultModel.scss";

type ResultAPI = {
    message: string;
    result: {
        attempt_uuid: string;
        course_uuid?: string | null;
        course_level_uuid?: string | null;
        course_lesson_uuid?: string | null;
        duration_str?: string | null;
        avg_speaking_ms?: number | null;
        avg_response_ms?: number | null;
        avg_score_pct?: number | null;
        proficiency_level?: number | null;
        conversation: Array<{
            id: string;
            role: "ATC" | "STUDENT";
            prompt?: string | null;
            slide_title?: string | null;
            expected_tokens?: string[];
            tokens?: Array<{
                text: string;
                state: "CORRECT" | "CORRECTED" | "INCORRECT";
            }>;
        }>;
        meta?: {
            user?: { id?: number; name?: string | null };
            course?: { id?: number; title?: string | null };
            ended_at?: string | null;
        };
    };
};

type Props = {
    show: boolean;
    attemptUuid: string;
    onClose: () => void;
    onRepeat: (attemptUuid: string, courseLessonUuid?: string | null) => void;
    onCorrect: (courseLessonUuid?: string | null) => void;
    onEndLesson: (courseLevelUuid?: string | null) => void;
};

const msToSec = (ms?: number | null) =>
    ms == null ? "-" : Math.max(0, Math.round(ms / 1000));
const clampPct = (v?: number | null) =>
    v == null ? 0 : Math.max(0, Math.min(100, Math.round(v)));

const LessonResultModal: React.FC<Props> = ({
    show,
    attemptUuid,
    onClose,
    onRepeat,
    onCorrect,
    onEndLesson,
}) => {
    const [loading, setLoading] = useState(true);
    const [payload, setPayload] = useState<ResultAPI["result"] | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [printedAt, setPrintedAt] = useState<string>("");

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            if (!show) return;
            setLoading(true);
            setErr(null);
            try {
                const res = (await WebService.getAPI({
                    action: `/training/attempts/${attemptUuid}/result`,
                })) as ResultAPI;
                if (mounted) setPayload(res.result);
            } catch {
                if (mounted) setErr("Failed to load results.");
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => {
            mounted = false;
        };
    }, [show, attemptUuid]);

    const pl = payload?.proficiency_level ?? 0;
    const avgSpeakSec = msToSec(payload?.avg_speaking_ms);
    const avgRespSec = msToSec(payload?.avg_response_ms);
    const avgScorePct = clampPct(payload?.avg_score_pct);

    function printReportIsolated() {
        const src = document.querySelector<HTMLElement>('.print-report');
        if (!src) { window.print(); return; }

        // clone page styles into the iframe so your SCSS still applies
        const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
            .map(el => el.outerHTML)
            .join('\n');

        const force = `
    @page { size: A4; margin: 14mm; }
    html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    /* just in case any screen-only rules leak in those styles */
    .screen-only { display: none !important; }
    /* ensure our report fills the page nicely */
    body { margin: 0; }
  `;

        const html = `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>Report</title>
        ${styles}
        <style>${force}</style>
      </head>
      <body>
        ${src.outerHTML}
      </body>
    </html>`;

        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow?.document;
        if (!doc) return;
        doc.open();
        doc.write(html);
        doc.close();

        iframe.onload = () => {
            // important: wait a microtask so stylesheets attach
            setTimeout(() => {
                iframe.contentWindow?.focus();
                iframe.contentWindow?.print();
                // clean up
                setTimeout(() => document.body.removeChild(iframe), 300);
            }, 50);
        };
    }
    // Screen gauge: start arc from top (-90deg)
    const arcStyle = useMemo(() => {
        const pct = avgScorePct; // ring based on transmission score
        // conic-gradient from accent to light gray
        return {
            background: `conic-gradient(#f59e0b ${pct * 3.6}deg, #e5e7eb 0deg)`,
        } as React.CSSProperties;
    }, [avgScorePct]);

    const expectedText = (row: ResultAPI["result"]["conversation"][number]) =>
        row.expected_tokens && row.expected_tokens.length
            ? row.expected_tokens.join(" ")
            : "--";

    const responsePlainText = (
        row: ResultAPI["result"]["conversation"][number]
    ) =>
        row.tokens && row.tokens.length
            ? row.tokens.map((t) => t.text).join(" ")
            : "--";

    const handlePrint = () => {
        setPrintedAt(new Date().toLocaleString());
        // Allow state to flush before opening print dialog
        setTimeout(() => window.print(), 20);
    };

    return (
        <Modal
            show={show}
            size="xl"
            centered
            dialogClassName="lrw-dialog"
            contentClassName="lrw-content"
            fullscreen="md-down"
            backdrop="static"           // <-- prevents dismiss on backdrop click
            keyboard={false}            // <-- prevents dismiss on ESC

        >
            {/* ===== SCREEN (old UI stays as-is) ===== */}
            <div className="lrw-root screen-only">
                {/* LEFT */}
                <div className="lrw-left">
                    <div className="lrw-title">Results</div>

                    <div className="lrw-gauge">
                        <div className="ring" style={arcStyle}>
                            <div className="ring-hole">
                                <div className="pl-level">PL{pl || 0}</div>
                                <div className="pl-sub">PROFICIENCY LEVEL</div>
                            </div>
                        </div>
                    </div>

                    <div className="lrw-metrics">
                        <div className="metric">
                            <div className="label">Duration</div>
                            <div className="value">{payload?.duration_str || "-"}</div>
                        </div>
                        <div className="metric">
                            <div className="label">Average Speaking</div>
                            <div className="value">{avgSpeakSec}s</div>
                        </div>
                        <div className="metric">
                            <div className="label">Average Response</div>
                            <div className="value">{avgRespSec}s</div>
                        </div>
                        <div className="metric">
                            <div className="label">Accuracy Score</div>
                            <div className="value">{avgScorePct}%</div>
                        </div>
                    </div>

                    <div className="lrw-actions">
                        <button
                            className="btn-link"
                            onClick={() =>
                                onRepeat(
                                    payload?.attempt_uuid || attemptUuid,
                                    payload?.course_lesson_uuid
                                )
                            }
                        >
                            Repeat Lesson
                        </button>
                        <button
                            className="btn-link"
                            onClick={() => onCorrect(payload?.course_lesson_uuid)}
                        >
                            Correct Lesson
                        </button>
                        <Button
                            className="btn-primary-wide"
                            onClick={() => onEndLesson(payload?.course_level_uuid)}
                        >
                            End Lesson
                        </Button>
                    </div>
                </div>

                {/* RIGHT */}
                <div className="lrw-right">
                    <div className="lrw-right-head">
                        <div className="title">Conversation History</div>
                        <div className="legend">
                            <span className="lg lg-green" /> Correct
                            <span className="lg lg-blue" /> Corrected
                            <span className="lg lg-red" /> Incorrect/Missing
                        </div>
                    </div>
                    <div className="lrw-list">
                        {loading && (
                            <div className="lrw-loading">
                                <Spinner animation="border" size="sm" className="me-2" />
                                Loading…
                            </div>
                        )}
                        {err && <div className="lrw-error">{err}</div>}
                        {!loading &&
                            !err &&
                            (payload?.conversation || []).map((row) => (
                                <div className="lrw-item" key={row.id}>
                                    <div className="lrw-item-head">
                                        <div className="role">ATC</div>
                                        <div className="qtitle qtitle-italic">
                                            {row.slide_title || row.prompt || ""}
                                        </div>
                                    </div>

                                    <div
                                        className="lrw-token-bar lrw-token-right"
                                        aria-label="User tokens"
                                    >
                                        {(row.tokens || []).map((t, i) => (
                                            <span
                                                key={i}
                                                className={`tk ${t.state === "CORRECT"
                                                    ? "ok"
                                                    : t.state === "CORRECTED"
                                                        ? "fix"
                                                        : "bad"
                                                    }`}
                                            >
                                                {t.text}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                    </div>

                    <div className="lrw-foot">
                        <Button variant="primary" className="btn-print" onClick={printReportIsolated}>
                            Print
                        </Button>
                    </div>
                </div>
            </div>

            {/* ===== PRINT (new one-page layout) ===== */}
            <div className="print-report print-only">
                {/* User + Date */}
                <div className="print-meta">
                    <div className="pm-left">
                        <strong>User:</strong> {payload?.meta?.user?.name || "-"}
                    </div>
                    <div className="pm-right">
                        <strong>Date:</strong> {printedAt || new Date().toLocaleString()}
                    </div>
                </div>

                {/* Summary */}
                <div className="summary-card">
                    <div className="summary-head">
                        <div className="sum-title">
                            RESULTS FOR {payload?.meta?.course?.title ?? "LEVEL"}
                        </div>
                    </div>
                    <div className="sum-body">
                        <div className="sum-metrics">
                            <div className="sum-item">
                                <div className="sum-icon">⏱</div>
                                <div className="sum-label">Duration</div>
                                <div className="sum-value">{payload?.duration_str || "-"}</div>
                            </div>
                            <div className="sum-item">
                                <div className="sum-icon">🧠</div>
                                <div className="sum-label">Knowledge</div>
                                <div className="sum-value">{avgScorePct}%</div>
                            </div>
                        </div>

                        {/* Print SVG gauge (starts from top via rotate(-90)) */}
                        <div className="sum-gauge">
                            {/* PRINT-ONLY SVG (fallback for conic-gradient) */}
                            <svg
                                className="ring-print"
                                viewBox="0 0 120 120"
                                width="160"
                                height="160"
                                aria-hidden="true"
                            >
                                {/* background track */}
                                <circle
                                    cx="60" cy="60" r="54"
                                    fill="none"
                                    stroke="#e5e7eb"
                                    strokeWidth="12"
                                />
                                {/* progress arc */}
                                {(() => {
                                    const r = 54;
                                    const C = 2 * Math.PI * r;
                                    const dash = (avgScorePct / 100) * C;
                                    return (
                                        <circle
                                            cx="60" cy="60" r={r}
                                            fill="none"
                                            stroke="#f59e0b"
                                            strokeWidth="12"
                                            strokeDasharray={`${dash} ${C}`}
                                            transform="rotate(-90 60 60)"
                                            strokeLinecap="round"
                                        />
                                    );
                                })()}
                                {/* center label */}
                                <circle cx="60" cy="60" r="44" fill="#fff" />
                                <text x="60" y="56" textAnchor="middle" fontSize="22" fontWeight="700" fill="#111827">
                                    PL{pl || 0}
                                </text>
                                <text x="60" y="76" textAnchor="middle" fontSize="8" fill="#6b7280">
                                    PROFICIENCY LEVEL
                                </text>
                            </svg>
                        </div>
                    </div>
                </div>

                {/* Conversation */}
                <div className="history-card">
                    <div className="history-head">
                        <div className="h-title">CONVERSATION HISTORY</div>
                        <div className="legend">
                            <span className="lg lg-green" /> CORRECT
                            <span className="lg lg-blue" /> CORRECTED
                            <span className="lg lg-red" /> INCORRECT/MISSING
                        </div>
                    </div>

                    <div className="history-list">
                        {loading && <div className="lrw-loading">Loading…</div>}
                        {err && <div className="lrw-error">{err}</div>}
                        {!loading &&
                            !err &&
                            (payload?.conversation || []).map((row) => (
                                <div className="h-item" key={row.id}>
                                    <div className="h-row-head">ATC</div>
                                    <div className="h-prompt">{row.slide_title || row.prompt || ""}</div>

                                    <div className="resp-panel">
                                        <div className="resp-right">
                                            <div className="resp-right-title">Your Response</div>

                                            {/* VALUE first, LABEL on the RIGHT (right-aligned) */}
                                            <div className="resp-line">
                                                <div className="resp-label">Expected Response:</div>
                                                <div className="resp-value">{expectedText(row)}</div>

                                            </div>

                                            <div className="resp-line">
                                                <div className="resp-label">Response:</div>
                                                <div className="resp-value">{responsePlainText(row)}</div>

                                            </div>

                                            <div className="resp-line">
                                                <div className="resp-label">Scored Response:</div>
                                                <div className="resp-value resp-scored">
                                                    {(row.tokens || []).length === 0
                                                        ? "--"
                                                        : (row.tokens || []).map((t, idx) => (
                                                            <span
                                                                key={idx}
                                                                className={`tk ${t.state === "CORRECT" ? "ok" : t.state === "CORRECTED" ? "fix" : "bad"
                                                                    }`}
                                                            >
                                                                {t.text}
                                                                {idx < (row.tokens || []).length - 1 ? " " : ""}
                                                            </span>
                                                        ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default LessonResultModal;
